"use client";

import { useEffect, useState } from "react";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Users, ChevronRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import type { Game } from "@/types/script";
import { TooltipProvider } from "@/components/ui/tooltip";
import truncate from "@/lib/truncate";
import { GenreIcon } from "./genre-icon";

interface GameWrapper {
  game: Game;
}

interface GameCarouselProps {
  games: GameWrapper[];
}

export default function GameCarousel({ games }: GameCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [playerCounts, setPlayerCounts] = useState<Record<string, number>>({});
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);

  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);

    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on("select", handleSelect);

    return () => {
      api.off("select", handleSelect);
    };
  }, [api]);

  useEffect(() => {
    const fetchPlayerCounts = async () => {
      if (!games || games.length === 0) return;

      const universeIds: string[] = games
        .map((wrapper) => {
          const game = wrapper.game.game;
          return game.universeId || game.placeId;
        })
        .filter(Boolean);

      if (universeIds.length === 0) return;

      setIsLoadingCounts(true);
      try {
        const response = await fetch(
          `https://games.roproxy.com/v1/games?universeIds=${universeIds.join(",")}`,
        );

        if (!response.ok) {
          console.warn("Failed to fetch player counts:", response.status);
          return;
        }

        const data = await response.json();
        const counts: Record<string, number> = {};

        data.data.forEach((game: any) => {
          counts[game.id.toString()] = game.playing || 0;
        });

        setPlayerCounts(counts);
      } catch (error) {
        console.error("Error fetching player counts:", error);
      } finally {
        setIsLoadingCounts(false);
      }
    };

    fetchPlayerCounts();
  }, [games]);

  const firstGame = games[0]?.game.game;

  return (
    <TooltipProvider>
      <div className="space-y-2">
        <h2 className="font-heading mb-2 text-balance text-base font-medium tracking-tight sm:text-lg lg:text-xl">
          Supported Games
        </h2>

        <div className="relative">
          <Carousel
            setApi={setApi}
            className="w-full"
            opts={{
              align: "start",
              loop: true,
            }}
          >
            <CarouselContent className="-ml-2">
              {games.slice(0, 8).map((wrapper, index) => {
                const game = wrapper.game.game;
                const universeId = game.universeId || game.placeId;
                const playerCount = playerCounts[universeId] || 0;

                return (
                  <CarouselItem
                    key={game.id || index}
                    className="basis-full pl-2 sm:basis-1/2 lg:basis-1/3"
                  >
                    <div className="overflow-hidden rounded-md transition-all duration-200 hover:shadow-md">
                      <Link href={`/game/${game.slug}`} className="block">
                        <AspectRatio
                          ratio={16 / 9}
                          className="overflow-hidden bg-background/30"
                        >
                          <Image
                            src={game.thumbnail || "/assets/cube.webp"}
                            alt={game.title}
                            className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                            fill
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            priority={index === 0}
                          />
                        </AspectRatio>
                      </Link>
                      <div className="rounded-md-b border border-white/5 bg-white/5 p-4">
                        <Link
                          href={`https://www.roproxy.com/games/${game.placeId}`}
                          className="transition-colors hover:text-primary"
                        >
                          <h3 className="font-heading mb-1.5 text-sm font-medium leading-tight">
                            {truncate({
                              text: game.title,
                              length: 50,
                            })}
                          </h3>
                        </Link>
                        <div className="flex items-center gap-2 text-sm">
                          {isLoadingCounts ? (
                            <Badge
                              variant="accent"
                              className="h-5 animate-pulse px-2"
                            >
                              <span className="opacity-70">Loading...</span>
                            </Badge>
                          ) : (
                            <Badge
                              variant="accent"
                              className="h-5 gap-1 px-2 font-normal"
                            >
                              <Users size={12} className="opacity-70" />
                              <span>{playerCount.toLocaleString()}</span>
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            •
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {game.creator || "Game"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <div className="absolute -right-2 top-1/2 -translate-y-1/2">
              <CarouselNext className="h-8 w-8 rounded-full border border-border/40 shadow-md" />
            </div>
            <div className="absolute -left-2 top-1/2 -translate-y-1/2">
              <CarouselPrevious className="h-8 w-8 rounded-full border border-border/40 shadow-md" />
            </div>
          </Carousel>

          {count > 1 && (
            <div className="mt-4 flex justify-center gap-1.5">
              {Array.from({ length: count }).map((_, i) => (
                <button
                  key={i}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    current === i
                      ? "w-6 bg-primary"
                      : "w-2 bg-muted hover:bg-muted-foreground/50"
                  }`}
                  onClick={() => {
                    api?.scrollTo(i);
                  }}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="card-bg overflow-hidden shadow-sm">
          <Link
            href={`/game/${firstGame.slug}`}
            className="group flex items-center p-4 transition-colors hover:bg-background/30"
          >
            <div className="mr-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <GenreIcon genre={firstGame.genre} />
            </div>
            <div className="flex-1 gap-y-1.5">
              <h3 className="font-heading text-sm font-medium">
                {firstGame.title || "Browse more scripts"}
              </h3>
              <p className="text-xs text-muted-foreground">
                Find more scripts for this game
              </p>
            </div>
            <div className="flex items-center text-xs font-medium text-primary/80 transition-colors group-hover:text-primary">
              BROWSE MORE
              <ChevronRight
                size={16}
                className="ml-1 transition-transform group-hover:translate-x-0.5"
              />
            </div>
          </Link>
        </div>
      </div>
    </TooltipProvider>
  );
}
